/**
 * B2BRoute — Auth guard for the /b2b sub-tree.
 *
 * Phase 38.3 architecture note
 * ────────────────────────────
 * B2BUserProvider is now hoisted ABOVE this component in App.tsx so that a
 * SINGLE context instance spans both the login route (/b2b) and every
 * authenticated route (/b2b/*).  Previously, each <B2BRoute> mounted its
 * own provider, so seedB2BUser() wrote into the login route's provider and
 * navigate('/b2b/agent') destroyed it and created a fresh, empty provider —
 * causing session = null on B2BClientSelection ("פרטי הסוכן לא נמצאו").
 *
 * B2BRoute is now used as a layout route (renders <Outlet />) rather than a
 * wrapper component with children props, so it no longer owns the provider.
 *
 * Behaviour:
 *   - While Supabase is still resolving the session → full-screen spinner.
 *   - Authenticated user landing on bare /b2b → redirect to /b2b/catalog.
 *   - All other paths → render <Outlet /> (child routes).
 */

import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/features/auth/AuthContext';
import { Loader2 } from 'lucide-react';

export function B2BRoute() {
  const { user, loading } = useAuth();
  const location          = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-primary-600">
        <Loader2 className="w-8 h-8 text-white animate-spin" />
      </div>
    );
  }

  // Already authenticated on bare /b2b root → redirect into the app.
  // B2BLayout's hard-refresh restore will re-classify and route correctly.
  if (user && location.pathname === '/b2b') {
    return <Navigate to="/b2b/catalog" replace />;
  }

  // Render the matched child route (login page or authenticated layout).
  return <Outlet />;
}
